import { useState, useRef, useEffect } from "react";
import { axiosInstance } from "@/App";
import { AnswerFeedback } from "@/components/help/AnswerFeedback";
import { Sparkles, Send, X, Loader2, ChevronDown } from "lucide-react";

/**
 * Floating chat widget — "Ask OC Tasker" — mounted on every admin page.
 * GPT-5.2-powered natural language queries against the live KPI snapshot.
 */
export default function AskOCTaskerWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);   // {role, text}
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const ask = async () => {
    const q = input.trim();
    if (!q || busy) return;
    setMessages((m) => [...m, { role: "user", text: q }]);
    setInput("");
    setBusy(true);
    try {
      const r = await axiosInstance.post("/admin/signals/nlq", {
        question: q,
        session_id: sessionId,
      });
      if (r.data.session_id) setSessionId(r.data.session_id);
      setMessages((m) => [...m, { role: "assistant", text: r.data.answer || "(no answer)", q }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", text: `Error: ${e.response?.data?.detail || "failed"}`, isError: true }]);
    }
    setBusy(false);
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); ask(); }
  };

  const SUGGESTIONS = [
    "How are we tracking on revenue this week?",
    "Any AI escalations needing follow-up?",
    "Which estimates are aging without approval?",
    "What's our weekend turnover backlog?",
  ];

  return (
    <>
      {/* Bubble */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-5 right-5 z-50 bg-gradient-to-br from-purple-600 to-fuchsia-600 hover:from-purple-700 hover:to-fuchsia-700 text-white rounded-full shadow-xl shadow-purple-500/30 px-4 py-3 flex items-center gap-2 transition-transform hover:scale-105"
          data-testid="ask-oc-tasker-bubble"
          aria-label="Open OC Tasker chat assistant"
        >
          <Sparkles className="w-4 h-4" />
          <span className="text-sm font-medium">Ask OC Tasker</span>
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          className="fixed bottom-5 right-5 z-50 w-[380px] max-w-[calc(100vw-2rem)] h-[520px] max-h-[calc(100vh-2rem)] bg-white rounded-xl shadow-2xl border border-purple-200 flex flex-col overflow-hidden"
          data-testid="ask-oc-tasker-panel"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-600 to-fuchsia-600 text-white px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4" />
              <div>
                <div className="font-semibold text-sm">Ask OC Tasker</div>
                <div className="text-[11px] text-white/80">GPT-5 · live data + how-to help</div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => { setMessages([]); setSessionId(null); }}
                className="p-1.5 rounded hover:bg-white/20 transition text-[10px]"
                title="Clear conversation"
                data-testid="ask-clear"
              >Clear</button>
              <button
                onClick={() => setOpen(false)}
                className="p-1.5 rounded hover:bg-white/20 transition"
                title="Minimize"
                data-testid="ask-close"
              >
                <ChevronDown className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-2 bg-gradient-to-b from-purple-50/40 to-white">
            {messages.length === 0 && (
              <div className="space-y-3">
                <div className="text-xs text-gray-600 leading-relaxed">
                  Hey! I have a snapshot of your live KPIs (revenue, estimates, AI escalations, reviews, weekend turnover, Turno billing). Ask me anything.
                </div>
                <div className="space-y-1.5">
                  <div className="text-[10px] uppercase tracking-wide text-purple-700/80 font-semibold">Try asking</div>
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      onClick={() => setInput(s)}
                      className="block w-full text-left text-xs text-purple-900 bg-purple-100 hover:bg-purple-200 transition px-2.5 py-1.5 rounded-md"
                      data-testid={`ask-suggestion-${s.slice(0, 12)}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap ${
                  m.role === "user"
                    ? "bg-purple-600 text-white rounded-br-sm"
                    : m.isError
                      ? "bg-red-50 text-red-800 border border-red-200 rounded-bl-sm"
                      : "bg-white border border-gray-200 text-gray-900 rounded-bl-sm"
                }`} data-testid={`ask-msg-${m.role}-${i}`}>
                  {m.text}
                  {m.role === "assistant" && m.q && <AnswerFeedback question={m.q} answer={m.text} testId={`ask-feedback-${i}`} />}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start">
                <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-sm px-3 py-2 text-sm text-gray-500 flex items-center gap-2">
                  <Loader2 className="w-3 h-3 animate-spin" /> Thinking…
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="border-t border-gray-200 p-2.5 bg-white">
            <div className="flex gap-1.5">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKey}
                placeholder="Ask about revenue, estimates, AI, reviews…"
                rows={1}
                className="flex-1 resize-none border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent max-h-24"
                data-testid="ask-input"
              />
              <button
                onClick={ask}
                disabled={busy || !input.trim()}
                className="bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 text-white rounded-lg px-3 transition"
                data-testid="ask-send"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </div>
            <div className="text-[9px] text-gray-400 mt-1.5 text-center">PII redacted · admin-only · sessions are logged</div>
          </div>
        </div>
      )}
    </>
  );
}
